/*
  File: src/components/product/VariantSelector.jsx
  Purpose: Present color and size selection based on variant catalog.
  Props:
  - variants: Array<{ color?: string, size?: string|number, stock?: number }>
  - selectedColor, onColorChange, selectedSize, onSizeChange
*/
import React, { useMemo } from 'react';

/**
 * VariantSelector
 * Renders color and size selectors based on variant data.
 */
function VariantSelector({
    variants,
    selectedColor,
    onColorChange,
    selectedSize,
    onSizeChange,
}) {
    const colorOptions = useMemo(
        () => Array.from(new Set(variants.map(v => v.color).filter(Boolean))),
        [variants]
    );

    const sizeOptions = useMemo(() => {
        const raw = variants
            .filter(v => (selectedColor ? v.color === selectedColor : true))
            .map(v => ({ label: String(v.size ?? ''), stock: v.stock ?? 0 }))
            .filter(x => x.label !== '')
            .reduce((acc, cur) => { if (!acc.find(a => a.label === cur.label)) acc.push(cur); return acc; }, []);
        const alphaOrder = ['S', 'M', 'L', 'XL'];
        const numOrder = ['40', '41', '42', '43'];
        const comboOrder = ['S/M', 'M/L', 'L/XL'];
        const score = (s) => {
            if (alphaOrder.includes(s)) return 0 + alphaOrder.indexOf(s);
            if (numOrder.includes(s)) return 100 + numOrder.indexOf(s);
            if (comboOrder.includes(s)) return 200 + comboOrder.indexOf(s);
            return 1000;
        };
        return raw.sort((a, b) => score(a.label) - score(b.label));
    }, [variants, selectedColor]);

    return (
        <div className="mt-6 space-y-3">
            {colorOptions.length > 0 && (
                <div>
                    <div className="mb-2 text-sm text-white/80">Color</div>
                    <select
                        value={selectedColor}
                        onChange={(e) => { onColorChange(e.target.value); onSizeChange(''); }}
                        className="w-48 px-3 py-2 rounded bg-black/40 border border-white/20"
                    >
                        {colorOptions.map(c => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                        {colorOptions.length === 0 ? (
                            <option value="">—</option>
                        ) : null}
                    </select>
                </div>
            )}
            <div>
                <div className="mb-2 text-sm text-white/80">Size</div>
                <div className="flex gap-2 flex-wrap">
                    {sizeOptions.map(({ label, stock }) => (
                        <button
                            key={label}
                            onClick={() => onSizeChange(label)}
                            disabled={stock <= 0}
                            className={`px-3 py-2 rounded border ${selectedSize === label ? 'bg-white/20 border-white' : 'bg-white/10 border-white/20 hover:bg-white/20'} ${stock <= 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
                            title={stock <= 0 ? 'Out of stock' : ''}
                        >
                            {label}
                        </button>
                    ))}
                    {variants.length === 0 && (
                        <div className="text-white/60 text-sm">No variants</div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default VariantSelector;


